import {
  useCallback, useMemo, useState,
} from 'react';
import { copy } from '~/_shared/utils/collections/collections';
import { useMapListingSearchQuery } from '~/store/frontendState/mapListing/mapListing.hook';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { type MapListSortType } from './mapListing.helpers';
import { useMapListingItems } from './useMapListingItems';

export const useMapListingToggleManagement = (sortType: MapListSortType) => {
  const currentUserId = useUserIdSelector();

  const [selectedMaps, setSelectedMaps] = useState<ReadonlyArray<{ id: number; name: string }>>([]);
  const [selectedSubMapIds, setSelectedSubMapIds] = useState<ReadonlySet<number>>(new Set());

  const { searchQuery, onSearchQueryChange } = useMapListingSearchQuery();

  const {
    mapListingItems, onToggleMapIdsChange, toggledMapIds, pagination, onPageChange, isLoading: isMapListingDataLoading,
    errorMessage, forceRefresh, changePrivacyLevel, allMapListingItems,
  } = useMapListingItems({ isActive: true, sortType, includeCurrentMapOnTop: true, searchQuery });

  const selectedMapIds: readonly number[] = useMemo(() => selectedMaps.map(map => map.id), [selectedMaps]);

  const setSelectedMapIds = useCallback((ids: readonly number[]) => {
    setSelectedMaps(ids.map(id => ({ id, name: mapListingItems.find(map => map.id === id)?.name ?? '' })));
  }, [mapListingItems]);

  const onToggleMapSelect = useCallback((mapId: number) => {
    const map = mapListingItems.find(m => m.id === mapId);
    if (!map) {
      return;
    }

    const subIds: ReadonlyArray<number> = map.snapshots.map(s => s.id);
    const operation = selectedMapIds.includes(mapId) ? copy.andRemove : copy.andAdd;

    setSelectedSubMapIds(prevIds => operation(prevIds, subIds));

    setSelectedMapIds(copy.andTogglePresence(selectedMapIds, mapId));
  }, [mapListingItems, selectedMapIds, setSelectedMapIds]);

  const onToggleSubMapSelect = useCallback((
    { mapId, subMapId }: Readonly<{ mapId: number; subMapId: number }>
  ) => {
    let newSubMapIds = copy.andTogglePresence(selectedSubMapIds, subMapId);

    if (selectedSubMapIds.has(subMapId)) {
      const mainMapOfSubMap = mapListingItems.find(m => m.id === mapId);
      if (mainMapOfSubMap) {
        newSubMapIds = copy.andRemove(newSubMapIds, mainMapOfSubMap.snapshots.filter(
          s => s.ownerId !== currentUserId
        ).map(s => s.id));
      }
      setSelectedMapIds(selectedMapIds.filter(id => id !== mapId));
    }

    setSelectedSubMapIds(newSubMapIds);
  }, [currentUserId, mapListingItems, selectedMapIds, selectedSubMapIds, setSelectedMapIds]);

  const onToggleShowSubMaps = useCallback((mapId: number) =>
    onToggleMapIdsChange(copy.andTogglePresence(toggledMapIds, mapId)),
  [onToggleMapIdsChange, toggledMapIds]);

  return {
    errorMessage,
    isLoading: isMapListingDataLoading,
    mapListingItems,
    onPageChange,
    onToggleMapSelect,
    onToggleShowSubMaps,
    onToggleSubMapSelect,
    pagination,
    selectedMapIds,
    selectedSubMapIds,
    selectedMaps,
    setSelectedMapIds,
    setSelectedSubMapIds,
    toggledMapIds,
    forceRefresh,
    searchQuery,
    onSearchQueryChange,
    changePrivacyLevel,
    allMapListingItems,
  };
};
